import { ADD_COLLABORATOR_TASK_DEFINITION } from '@bridebook/web/components/task/today-task/add-collaborator-task/task-definition';
import { MARK_AS_BOOKED_TASK_DEFINITION } from '@bridebook/web/components/task/today-task/mark-as-booked-task/task-definition';
import { SEARCH_PHOTOGRAPHERS_TASK_DEFINITION } from '@bridebook/web/components/task/today-task/search-photographers-task/task-definition';
import { CountryCodes } from '@bridebook/toolbox/src/gazetteer';
import { isTodayTaskCompleted } from '@bridebook/web/lib/task/selectors';
import { registerTodayTask, TTaskDefinitionUI } from '@bridebook/web/lib/task/task-flow-engine';
import {
  getVenueBooked,
  getWebsiteSections,
  getWeddingWebsiteId,
  getWeddingWebsiteSections,
} from '@bridebook/web/lib/weddings/selectors';
import { ALLOWED_INVITES_AND_PAPER_COUNTRY_CODES } from '@bridebook/web/lib/bbcommon/constants';

const hasGiftListUrl = (sections: ReturnType<typeof getWeddingWebsiteSections>) =>
  sections?.some(
    (section) =>
      section.type === 'GiftListField' &&
      typeof section.data?.url === 'string' &&
      section.data.url.trim().length > 0,
  );

const interactionTask = (
  id: string,
  name: string,
  isTaskCompleted?: TTaskDefinitionUI['isTaskCompleted'],
): TTaskDefinitionUI => {
  const def: TTaskDefinitionUI = {
    id,
    storage: 'wedding',
    name,
    type: 'interaction',
    isTaskCompleted: isTaskCompleted ?? (({ getState }) => isTodayTaskCompleted(getState(), def)),
  };
  return def;
};

// Task name strings below are persisted in Firestore — do not rename without migration.
export const ASSIGNMENT_VENUE_SELECTED_TASK_DEFINITION = interactionTask(
  'a1b2c3d4-e5f6-4a7b-8c9d-0e1f2a3b4c5d',
  'Mobile assignment (venue selected)',
);

export const ASSIGNMENT_VENUE_NOT_SELECTED_TASK_DEFINITION = interactionTask(
  'b2c3d4e5-f6a7-4b8c-9d0e-1f2a3b4c5d6e',
  'Mobile assignment (venue not selected)',
);

export const START_EXPLORING_VENUES_TASK_DEFINITION = interactionTask(
  '5c3de2be-320b-40fa-8ca4-bb3fe57428d8',
  'Start exploring venues',
  ({ getState, market }) => {
    if (!market.hasVenueDirectory) return true;
    return isTodayTaskCompleted(getState(), START_EXPLORING_VENUES_TASK_DEFINITION);
  },
);

export const REACH_OUT_VENUES_TASK_DEFINITION = interactionTask(
  '9b46033c-ae0c-4e46-a660-c8eaa2d9d81f',
  'Reach out to favourite venues',
  ({ getState, market }) => {
    if (!market.hasVenueDirectory) return true;
    return isTodayTaskCompleted(getState(), REACH_OUT_VENUES_TASK_DEFINITION);
  },
);

export const ARRANGE_VIEWING_TASK_DEFINITION = interactionTask(
  '926f5891-3231-4ac0-afc6-127a9c45db79',
  'Arrange a viewing',
  ({ getState, market }) => {
    if (!market.hasVenueDirectory) {
      return true;
    }
    return isTodayTaskCompleted(getState(), ARRANGE_VIEWING_TASK_DEFINITION);
  },
);

export const EXPLORE_SUPPLIERS_TASK_DEFINITION = interactionTask(
  '59f9c3d6-d14e-4484-8994-2711a9c8a332',
  'Explore suppliers',
  ({ getState, market }) => {
    if (!market.hasSupplierDirectory) return true;
    return isTodayTaskCompleted(getState(), EXPLORE_SUPPLIERS_TASK_DEFINITION);
  },
);
EXPLORE_SUPPLIERS_TASK_DEFINITION.mirrorTask = SEARCH_PHOTOGRAPHERS_TASK_DEFINITION;

export const REACH_OUT_SUPPLIERS_TASK_DEFINITION = interactionTask(
  '082642b9-af18-417c-b3c5-beb513af8dac',
  'Reach out to favourite suppliers',
  ({ getState, market }) => {
    if (!market.hasSupplierDirectory) return true;
    return isTodayTaskCompleted(getState(), REACH_OUT_SUPPLIERS_TASK_DEFINITION);
  },
);

export const BUILD_WEDDING_WEBSITE_TASK_DEFINITION = interactionTask(
  '28639920-9d3a-40e1-b36f-028b8107a5e7',
  'Build your wedding website',
  ({ getState }) => {
    const state = getState();
    return (
      Boolean(getWeddingWebsiteId(state)) ||
      isTodayTaskCompleted(state, BUILD_WEDDING_WEBSITE_TASK_DEFINITION)
    );
  },
);

export const SETUP_GIFT_LIST_TASK_DEFINITION = interactionTask(
  '9ebcc470-b7ab-4baf-a7cf-8b103af99d0e',
  'Set up a gift list',
  ({ getState }) => {
    const state = getState();
    return (
      hasGiftListUrl(getWeddingWebsiteSections(state)) ||
      hasGiftListUrl(getWebsiteSections(state)) ||
      isTodayTaskCompleted(state, SETUP_GIFT_LIST_TASK_DEFINITION)
    );
  },
);

export const LOOK_INTO_WEDDING_INSURANCE_TASK_DEFINITION = interactionTask(
  'd051bebf-0d5d-4585-b7c9-ac84cae8dcd2',
  'Look into wedding insurance',
  ({ getState, market }) => {
    if (market.country !== CountryCodes.GB) return true;
    return isTodayTaskCompleted(getState(), LOOK_INTO_WEDDING_INSURANCE_TASK_DEFINITION);
  },
);

export const INVITES_AND_PAPER_TASK_DEFINITION = interactionTask(
  'b5ee1718-65a7-4448-b348-2a106200dc34',
  'Invites and paper products',
  ({ getState, market }) => {
    if (!ALLOWED_INVITES_AND_PAPER_COUNTRY_CODES.includes(market.country)) return true;
    return isTodayTaskCompleted(getState(), INVITES_AND_PAPER_TASK_DEFINITION);
  },
);

export const EXPLORE_ADVICE_TASK_DEFINITION: TTaskDefinitionUI = {
  id: 'dfab0524-d843-4285-8b98-6f8ba72bda9c',
  storage: 'none',
  name: 'Explore advice',
  type: 'interaction',
  isTaskCompleted: () => false,
};

// Mobile-flow-specific copies of control tasks (different IDs to avoid breaking existing users' completion state)
export const MOBILE_MARK_AS_BOOKED_TASK_DEFINITION = interactionTask(
  'a528ca37-6f38-4bc7-b15d-9f8d1ac44981',
  'Mark venue as booked',
  ({ getState, market }) => !market.hasVenueDirectory || getVenueBooked(getState()),
);
MOBILE_MARK_AS_BOOKED_TASK_DEFINITION.mirrorTask = MARK_AS_BOOKED_TASK_DEFINITION;

export const MOBILE_ADD_COLLABORATOR_TASK_DEFINITION = interactionTask(
  'efe8235f-79bd-48ed-a7ba-391165445838',
  'Add collaborator task',
  ({ getState }) => {
    const state = getState();
    return (
      isTodayTaskCompleted(state, MOBILE_ADD_COLLABORATOR_TASK_DEFINITION) ||
      isTodayTaskCompleted(state, ADD_COLLABORATOR_TASK_DEFINITION)
    );
  },
);
MOBILE_ADD_COLLABORATOR_TASK_DEFINITION.mirrorTask = ADD_COLLABORATOR_TASK_DEFINITION;

[
  ASSIGNMENT_VENUE_SELECTED_TASK_DEFINITION,
  ASSIGNMENT_VENUE_NOT_SELECTED_TASK_DEFINITION,
  START_EXPLORING_VENUES_TASK_DEFINITION,
  REACH_OUT_VENUES_TASK_DEFINITION,
  ARRANGE_VIEWING_TASK_DEFINITION,
  EXPLORE_SUPPLIERS_TASK_DEFINITION,
  REACH_OUT_SUPPLIERS_TASK_DEFINITION,
  BUILD_WEDDING_WEBSITE_TASK_DEFINITION,
  SETUP_GIFT_LIST_TASK_DEFINITION,
  LOOK_INTO_WEDDING_INSURANCE_TASK_DEFINITION,
  INVITES_AND_PAPER_TASK_DEFINITION,
  MOBILE_MARK_AS_BOOKED_TASK_DEFINITION,
  MOBILE_ADD_COLLABORATOR_TASK_DEFINITION,
  EXPLORE_ADVICE_TASK_DEFINITION,
].forEach(registerTodayTask);
